import { useMutation, useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import vozillaIcon from '@/../resources/build/icon.png'
import { downloadVozillaAsset, fetchLatestVozillaRelease } from './release-distribution-client'
import { getPrimaryDownload, type DownloadItem, type Platform } from './release-assets'

function detectPlatform(): Platform {
  return navigator.userAgent.toLowerCase().includes('win') ? 'windows' : 'linux'
}

function getPlatformLabel(platform: Platform): string {
  return platform === 'windows' ? 'Windows' : 'Linux'
}

function LinuxIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2a.672.672 0 00.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 00-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139z" />
    </svg>
  )
}

function WindowsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
    </svg>
  )
}

function DownloadRow({
  item,
  downloading,
  onDownload,
}: {
  item: DownloadItem
  downloading: boolean
  onDownload: (item: DownloadItem) => void
}) {
  return (
    <button
      type="button"
      disabled={downloading}
      onClick={() => onDownload(item)}
      className="group -mx-3 flex w-[calc(100%+1.5rem)] items-center justify-between rounded px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-secondary/50 hover:text-foreground disabled:cursor-wait disabled:opacity-60"
    >
      <span>{downloading ? 'Preparing download…' : item.label}</span>
      <Download className="h-4 w-4 opacity-50 transition-opacity group-hover:opacity-100" />
    </button>
  )
}

function PlatformSection({
  platform,
  downloads,
  activeFilename,
  onDownload,
}: {
  platform: Platform
  downloads: DownloadItem[]
  activeFilename?: string
  onDownload: (item: DownloadItem) => void
}) {
  return (
    <div className="min-w-[200px] flex-1">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
        {platform === 'windows' ? <WindowsIcon className="h-4 w-4" /> : <LinuxIcon className="h-4 w-4" />}
        <span>{getPlatformLabel(platform)}</span>
      </div>
      <div className="space-y-0.5">
        {downloads.map((item) => (
          <DownloadRow
            key={item.filename}
            item={item}
            downloading={activeFilename === item.filename}
            onDownload={onDownload}
          />
        ))}
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="animate-pulse border-b border-border/30 py-4">
      <div className="mb-6 h-6 w-20 rounded bg-white/[0.06]" />
      <div className="h-32 rounded-lg border border-white/[0.06] bg-white/[0.04]" />
    </div>
  )
}

export function DownloadsPage() {
  const { session } = useAuth()
  const accessToken = session?.access_token
  const platform = detectPlatform()

  const releaseQuery = useQuery({
    queryKey: [
      'releases',
      'vozilla',
      { channel: 'main', userId: session?.user.id, sessionExpiresAt: session?.expires_at },
    ],
    enabled: Boolean(accessToken),
    queryFn: () => fetchLatestVozillaRelease(accessToken!),
  })

  const downloadMutation = useMutation({
    mutationFn: async (item: DownloadItem) => {
      if (!accessToken) throw new Error('Sign in to download Vozilla')
      await downloadVozillaAsset(item, accessToken)
    },
  })

  const release = releaseQuery.data
  const primaryDownload = release ? getPrimaryDownload(release.downloads, platform) : null
  const activeFilename = downloadMutation.isPending ? downloadMutation.variables?.filename : undefined
  const error = releaseQuery.error ?? downloadMutation.error

  return (
    <div>
      <div className="mb-12 flex items-start gap-6">
        <img src={vozillaIcon} alt="Vozilla" className="h-16 w-16 flex-shrink-0 rounded-xl" />
        <div>
          <h1 className="text-xl font-medium tracking-tight text-foreground">Download Vozilla</h1>
          <p className="mt-1 text-sm text-muted-foreground/70">Available for Windows and Linux</p>
          {primaryDownload && (
            <button
              type="button"
              disabled={downloadMutation.isPending}
              onClick={() => downloadMutation.mutate(primaryDownload)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border/50 bg-secondary px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary/80 disabled:cursor-wait disabled:opacity-60"
            >
              <Download className="h-4 w-4" />
              {activeFilename === primaryDownload.filename
                ? 'Preparing download…'
                : `Download for ${getPlatformLabel(platform)}`}
            </button>
          )}
        </div>
      </div>

      {releaseQuery.isLoading && <LoadingState />}

      {error && <div className="py-12 text-center text-destructive">{error.message}</div>}

      {release && !releaseQuery.isLoading && (
        <div className="border-b border-border/30 pb-6">
          <div className="flex items-center gap-3 py-4">
            <span className="text-lg font-medium text-foreground">{release.version}</span>
            <span className="rounded border border-border/50 px-2 py-0.5 text-xs text-muted-foreground">Latest</span>
          </div>
          <div className="flex flex-wrap gap-8 rounded-lg border border-border/30 bg-secondary/20 p-6">
            <PlatformSection
              platform="windows"
              downloads={release.downloads.windows}
              activeFilename={activeFilename}
              onDownload={(item) => downloadMutation.mutate(item)}
            />
            <PlatformSection
              platform="linux"
              downloads={release.downloads.linux}
              activeFilename={activeFilename}
              onDownload={(item) => downloadMutation.mutate(item)}
            />
          </div>
        </div>
      )}
    </div>
  )
}
